import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-600 mb-4">你查找的文章不存在或已被移除。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm hover:bg-primary-600 transition-colors">
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catBadgeColor =
    article.category === 'pingtai-tuijian'
      ? 'bg-accent-100 text-accent-700'
      : article.category === 'liaotian-huashu'
        ? 'bg-secondary-100 text-secondary-700'
        : 'bg-primary-100 text-primary-700';

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            <header className="mb-8">
              <span className={`inline-block text-xs px-2.5 py-1 rounded-full ${catBadgeColor} mb-4`}>
                {article.categoryName}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </header>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 约炮攻略`}
                className="w-full object-cover rounded-lg"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <h4 className="text-sm font-medium text-foreground-500 mb-3">标签</h4>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
                相关文章推荐
              </h2>
              <div className="space-y-3">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            image: article.imageUrl,
            publisher: {
              '@type': 'Organization',
              name: '约炮攻略',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://pairfinder.github.io/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.categoryName,
                item: `https://pairfinder.github.io/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}